import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const img = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

const Hero = () => (
  <section className="px-4 md:px-8 pt-8 md:pt-14 pb-10 md:pb-16 border-b-4 border-[#1C1914]" data-testid="shop-hero">
    <p
      className="font-condensed text-sm md:text-lg uppercase tracking-widest text-[#4A5D23]"
      data-testid="shop-hero-eyebrow"
    >
      // Apparel for the perpetually underwhelmed
    </p>
    <h1
      className="font-display text-[20vw] md:text-[12rem] leading-[0.85] uppercase mt-3"
      data-testid="shop-hero-title"
    >
      Shop the<br />sour drop.
    </h1>
    <p className="mt-6 font-body text-base md:text-lg max-w-xl text-[#1C1914]/80" data-testid="shop-hero-subtitle">
      Heavyweight tees, hoodies and crews. Embroidered, screen-printed, drawn poorly on purpose.
      Apple Pay & Google Pay accepted because we know you're shopping from your phone.
    </p>
    <div className="mt-6 flex flex-wrap gap-2" data-testid="shop-hero-promos">
      <span className="sticker tape-rotate-1">Buy 3 — 10% off</span>
      <span className="sticker tape-rotate-2" style={{ backgroundColor: "#4A5D23", color: "#FFFDF5" }}>
        Free shipping over $150
      </span>
    </div>
  </section>
);

const ProductCard = ({ p, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.4, delay: i * 0.04 }}
    className="group"
    data-testid={`shop-card-${p.id}`}
  >
    <Link to={`/product/${p.slug}`} className="block">
      <div className="border-4 border-[#1C1914] bg-[#FFFDF5] shadow-hard-sm group-hover:shadow-hard transition-shadow">
        <div className="aspect-square overflow-hidden bg-[#FFFDF5] border-b-2 border-[#1C1914]">
          <img
            src={img(p.images?.[0])}
            alt={p.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
            data-testid={`shop-card-image-${p.id}`}
          />
        </div>
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-display text-2xl md:text-3xl uppercase leading-none" data-testid={`shop-card-name-${p.id}`}>
              {p.name}
            </h3>
            {(p.colors && p.colors.length > 1) ? (
              <div className="flex -space-x-1 flex-shrink-0 mt-1" aria-label={`${p.colors.length} colours available`}>
                {p.colors.slice(0, 4).map((c) => (
                  <span
                    key={c.name}
                    className="inline-block w-5 h-5 border-2 border-[#1C1914]"
                    style={{ backgroundColor: c.chip }}
                    title={c.name}
                  />
                ))}
              </div>
            ) : (
              <span
                className="inline-block w-5 h-5 border-2 border-[#1C1914] flex-shrink-0 mt-1"
                style={{ backgroundColor: p.color_chip }}
                aria-label={`Shirt colour ${p.shirt_color}`}
              />
            )}
          </div>
          <p className="font-condensed text-base md:text-lg uppercase tracking-wide text-[#4A5D23] mt-2 leading-tight" data-testid={`shop-card-tagline-${p.id}`}>
            {p.tagline}
          </p>
          <div className="flex items-center justify-between mt-4 pt-3 border-t-2 border-dashed border-[#1C1914]/40">
            <span className="font-display text-2xl" data-testid={`shop-card-price-${p.id}`}>
              A${p.price_usd.toFixed(0)}
            </span>
            <span className="font-condensed text-xs uppercase tracking-widest text-[#D95D39]">
              {p.status}
            </span>
          </div>
        </div>
      </div>
    </Link>
  </motion.div>
);

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/products`);
        setProducts(res.data || []);
      } catch (e) {
        console.error("[shop] failed to load products:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="shop-page">
      <Hero />
      <section className="px-4 md:px-8 py-10 md:py-16" data-testid="shop-grid-section">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display text-4xl md:text-5xl uppercase" data-testid="shop-grid-title">
            Drop 001
          </h2>
          <span className="font-condensed text-sm uppercase tracking-widest text-[#1C1914]/70">
            {products.length} pieces
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="shop-grid">
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-[3/4] border-4 border-[#1C1914] bg-[#FFFDF5] animate-pulse"
                  data-testid={`shop-skeleton-${i}`}
                />
              ))
            : products.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
        </div>
      </section>
    </div>
  );
};

export default ShopPage;
